import React, { useRef, useEffect, useState } from 'react';
import joiPulsatingIcon from '../../../Assets/jogpt_pulsating_icon.svg';

interface ThinkingIndicatorProps {
  thinkingText: string;
}

const STATUS_PHRASES = [
  'Spinning up AI agents...',
  'Analyzing sources...',
  'Synthesizing insights...',
  'Consulting knowledge base...',
  'Connecting the dots...',
];

const ThinkingIndicator: React.FC<ThinkingIndicatorProps> = ({ thinkingText }) => {
  const thinkingPreRef = useRef<HTMLPreElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [fadeKey, setFadeKey] = useState(0);

  useEffect(() => {
    if (thinkingPreRef.current && isOpen) {
      thinkingPreRef.current.scrollTop = thinkingPreRef.current.scrollHeight;
    }
  }, [thinkingText, isOpen]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex(i => (i + 1) % STATUS_PHRASES.length);
      setFadeKey(k => k + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  if (thinkingText) {
    return (
      <div className="message assistant thinking-stream">
        <div className="message-content">
          <div className="message-avatar joi-avatar">
            <img src={joiPulsatingIcon} alt="JOI thinking" className="joi-icon-pulsating" />
          </div>
          <div className="thinking-box" role="status" aria-live="polite" aria-label="Deep search in progress">

            {/* Live badge + scan bar */}
            <div className="thinking-live-bar">
              <span className="thinking-live-dot" aria-hidden="true" />
              <span className="thinking-live-label">Deep Search</span>
              <div className="thinking-scan-track">
                <div className="thinking-scan-fill" />
              </div>
            </div>

            {/* Rotating status phrase */}
            <div className="thinking-status-phrase" key={fadeKey}>
              {STATUS_PHRASES[phraseIndex]}
            </div>

            {/* Collapsible log toggle */}
            <button
              className="thinking-log-toggle"
              onClick={() => setIsOpen(o => !o)}
              aria-expanded={isOpen}
            >
              <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true" />
              <span>{isOpen ? 'Hide logs' : 'View logs'}</span>
            </button>

            {isOpen && (
              <pre ref={thinkingPreRef} className="thinking-pre">
                {thinkingText}
              </pre>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="typing-message">
      <div className="message-content">
        <div className="message-avatar joi-avatar">
          <img src={joiPulsatingIcon} alt="JOI thinking" className="joi-icon-pulsating" />
        </div>
        <div
          className="typing-indicator"
          role="status"
          aria-live="polite"
          aria-label="Assistant is thinking"
        >
          Thinking
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
          <span aria-hidden="true"></span>
        </div>
      </div>
    </div>
  );
};

export default ThinkingIndicator;